import { Button, notification } from 'antd';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import type { ProcessingLockStatus } from '@repo/contracts/common';

import { OPERATION_TYPE_KEY_MAP } from '@/features/batch-operations/utils';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useScope } from '@/hooks/use-scope';
import { useUrlPrefix } from '@/hooks/use-url-prefix';
import { useAuth } from '@/lib/auth';
import { tenantApi } from '@/redux/api/tenant-api';

type ProcessingLockContextType = ProcessingLockStatus & {
  /** True when transitioning from locked → unlocked (for cache invalidation) */
  justUnlocked: boolean;
};

const ProcessingLockContext = createContext<
  ProcessingLockContextType | undefined
>(undefined);

const DEFAULT_STATUS: ProcessingLockStatus = { isLocked: false };

export function ProcessingLockProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, accessToken } = useAuth();
  const { tenantSlug } = useScope();
  const dispatch = useDispatch();
  const router = useProgressRouter();
  const decorateUrl = useUrlPrefix();
  const { t } = useTranslation();

  const [status, setStatus] = useState<ProcessingLockStatus>(DEFAULT_STATUS);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const prevLockedRef = useRef(false);
  const prevOperationTypeRef = useRef<string | undefined>(undefined);
  const prevBatchOperationIdRef = useRef<string | undefined>(undefined);
  const routerRef = useRef(router);
  const decorateUrlRef = useRef(decorateUrl);
  const tRef = useRef(t);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs in sync
  routerRef.current = router;
  decorateUrlRef.current = decorateUrl;
  tRef.current = t;

  // Track operation type and batch operation id from SSE messages
  useEffect(() => {
    if (status.operationType) {
      prevOperationTypeRef.current = status.operationType;
    }
    if (status.batchOperationId) {
      prevBatchOperationIdRef.current = status.batchOperationId;
    }
  }, [status.operationType, status.batchOperationId]);

  // Invalidate caches and show completion notification when lock releases
  useEffect(() => {
    if (prevLockedRef.current && !status.isLocked) {
      setJustUnlocked(true);
      dispatch(
        tenantApi.util.invalidateTags([
          'Flight',
          'FlightGroup',
          'BatchOperation',
        ]),
      );

      const currentT = tRef.current;
      const opKey =
        prevOperationTypeRef.current &&
        OPERATION_TYPE_KEY_MAP[prevOperationTypeRef.current]
          ? OPERATION_TYPE_KEY_MAP[prevOperationTypeRef.current]
          : 'default';
      const operationLabel = currentT(
        `batchOperationsShortcut.operationLabels.${opKey}`,
      );
      const batchOpId = prevBatchOperationIdRef.current;

      notification.success({
        message: currentT('batchOperationsShortcut.completed'),
        description: currentT('batchOperationsShortcut.completedDescription', {
          operation: operationLabel,
        }),
        btn: batchOpId ? (
          <Button
            type="link"
            size="small"
            onClick={() => {
              routerRef.current.push(
                decorateUrlRef.current(`/batch-operations/${batchOpId}`),
              );
              notification.destroy();
            }}
          >
            {currentT('batchOperationsShortcut.viewDetails')}
          </Button>
        ) : undefined,
        duration: 8,
      });

      const timeout = setTimeout(() => setJustUnlocked(false), 3000);
      return () => clearTimeout(timeout);
    }
    prevLockedRef.current = status.isLocked;
  }, [status.isLocked, dispatch]);

  const connect = useCallback(() => {
    if (!isAuthenticated || !tenantSlug || !accessToken) return;

    // Clear any pending reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Close existing connection
    eventSourceRef.current?.close();

    const baseUrl =
      import.meta.env.VITE_TENANT_API_SSE_URL ||
      import.meta.env.VITE_TENANT_API_URL ||
      'http://localhost:3012';
    const url = `${baseUrl}/batch-operations/processing-status/sse?token=${encodeURIComponent(accessToken)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data === 'object' && 'isLocked' in data) {
          setStatus(data as ProcessingLockStatus);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      // Reconnect after 5 seconds using ref to avoid stale closure
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, 5000);
    };
  }, [isAuthenticated, tenantSlug, accessToken]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connect]);

  return (
    <ProcessingLockContext.Provider value={{ ...status, justUnlocked }}>
      {children}
    </ProcessingLockContext.Provider>
  );
}

export function useProcessingLock(): ProcessingLockContextType {
  const context = useContext(ProcessingLockContext);
  if (!context) {
    throw new Error(
      'useProcessingLock must be used within a ProcessingLockProvider',
    );
  }
  return context;
}
